<template>
  <footer id="mainFooter">
    <div class="containerMain">
      <div class="mainFooter flex">
        <NuxtLink to="/" class="footerLogo">
          <img src="~/assets/images/footer_logo.svg" alt="codepresso's symbol logo">
        </NuxtLink>
        <div class="footerMenu">
          <div
            v-for="(menu, index) in menuList"
            :key="index" class="menuItem"
            :class="{on: menu.openFlag && !isWideScreen}"
          >
            <h5 v-if="menu.link">
              <NuxtLink
                :to="menu.link"
                :target="menu.external ? '_blank' : undefined"
                :rel="menu.external ? 'noopener' : undefined"
              >{{ methods.menuLabel(menu) }}</NuxtLink>
            </h5>
            <template v-else>
            <h5 @click.stop="methods.onClickMenu(menu.title)">
              {{ methods.menuLabel(menu) }} <span><svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10" fill="none">
              <path d="M1.70711 0.292893C1.31658 -0.0976318 0.683419 -0.0976318 0.292895 0.292893C-0.0976298 0.683417 -0.0976298 1.31658 0.292894 1.70711L1.70711 0.292893ZM5 5L5.70711 5.70711L6.41421 5L5.70711 4.29289L5 5ZM0.292893 8.29289C-0.0976309 8.68342 -0.097631 9.31658 0.292893 9.70711C0.683417 10.0976 1.31658 10.0976 1.70711 9.70711L0.292893 8.29289ZM0.292894 1.70711L4.29289 5.70711L5.70711 4.29289L1.70711 0.292893L0.292894 1.70711ZM4.29289 4.29289L0.292893 8.29289L1.70711 9.70711L5.70711 5.70711L4.29289 4.29289Z" fill="white" fill-opacity="0.6"/>
            </svg></span>
            </h5>
            <ul>
              <li v-for="(subMenu, subIndex) in menu.subTitle" :key="subIndex">
                <!-- 새 탭 외부 링크는 헤더 GNB 와 같은 rel="noopener" — noreferrer 를 붙이면 블로그 유입 통계에 출처(codepresso.io)가 남지 않는다 -->
                <NuxtLink
                  :to="subMenu.link"
                  :target="subMenu.external ? '_blank' : undefined"
                  :rel="subMenu.external ? 'noopener' : undefined"
                >{{ methods.menuLabel(subMenu) }}</NuxtLink>
              </li>
            </ul>
            </template>
          </div>
        </div>
        <div class="footerBottom">
          <div class="bottomArea">
            <ul class="flex">
              <li class="policy-pair">
                <NuxtLink to="/policy?tab=0">{{ t("terms-of-use") }}</NuxtLink>
                <NuxtLink to="/policy?tab=1">{{ t("legal-notice") }}</NuxtLink>
              </li>
              <li class="policy-pair">
                <NuxtLink to="/policy?tab=2">{{ t("privacy-policy") }}</NuxtLink>
                <NuxtLink to="/policy?tab=3">{{ t("cookie-policy") }}</NuxtLink>
              </li>
              <li v-if="$i18n.locale.value === 'ko'" class="info-row">
                  <div>상호 <span>코드프레소</span></div> <div class="separator">/</div>
                <div>대표자명 <span>이동훈</span></div> <div class="separator">/</div>
                <div>사업자등록번호 <span>677-88-01409</span></div> <div class="separator">/</div>
                <div>통신판매업 신고번호 <span>제 2022-서울강남-06544호</span></div> <div class="separator">/</div>
                <div>직업정보제공사업 신고번호 <span>J1200020240019</span></div>
              </li>
              <li class="info-row">
                <template v-if="$i18n.locale.value === 'ko'">
                  <div>주소 <span>경기도 성남시 수정구 금토로80번길 56, 위든타워 2층 B구역</span></div> <div class="separator">/</div>
                  <div>전화번호 <span>02-6954-7733</span></div> <div class="separator">/</div>
                </template>
                <address class="flex">{{ t("account-email") }}<a href="mailto:growth@codepresso.kr">support@codepresso.io</a></address>
              </li>
              <li>{{ copyright }}</li>
            </ul>
            <div class="rightArea flex">
              <ul class="snsArea flex">
                <li><a href="https://www.linkedin.com/company/codepresso/" target="_blank" class="sns4"></a></li>
              </ul>
              <div ref="langArea" class="langSelect">
                <div class="downToggle" :class="{on: isActive}" @click="isActive = !isActive">
                  <div class="icon"></div>
                  <div class="lang">
                    {{ LocalLanguagesType[$i18n.locale.value] || "English" }}
                  </div>
                </div>
                <ul class="scrollY" :class="{on: isActive}">
                  <li v-for="(lang, index) of keysOfLanguages" :key="index" :class="methods.setActiveClass(lang)" @click.stop="methods.changeLocale(lang)">
                    {{ LocalLanguagesType[lang] }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import {computed, ref} from "#imports";
import {useNuxtApp} from "nuxt/app";
import LocalLanguagesType from "~~/package/enum/LocalLanguagesType";
import {useI18n} from "vue-i18n";
import {useUserService} from "~~/application/composables/service/useUserService";
import {useUserStore} from "~/store/user/userStore";
import type {PropType} from "vue";
import type {HomeMenuType} from "~~/package/config/HomeSubMenuConfig";
import {onClickOutside} from '@vueuse/core';
import { useRenewalText } from "~~/codepresso-renewal/composables/useRenewalText";
import navKo from "~~/codepresso-renewal/data/i18n-dist/Nav/ko.json";
import navEn from "~~/codepresso-renewal/data/i18n-dist/Nav/en.json";
import navJa from "~~/codepresso-renewal/data/i18n-dist/Nav/ja.json";
import navVi from "~~/codepresso-renewal/data/i18n-dist/Nav/vi.json";
import navEs from "~~/codepresso-renewal/data/i18n-dist/Nav/es.json";

const {t} = useI18n({useScope: 'local'})
// 헤더 GNB(renewal SiteNav)와 같은 사전·같은 언어 해석 — 메뉴 문구가 언어마다 헤더와 똑같이 나온다.
const navText = useRenewalText({ ko: navKo, en: navEn, ja: navJa, vi: navVi, es: navEs });
const { $i18n } = useNuxtApp();
const prop = defineProps({
  menuList: { type: Array as PropType<HomeMenuType[]>, default: () => [] },
});
const emit = defineEmits(["onClickMenu"]);

const isActive = ref(false);
const userStore = useUserStore();
const userService = useUserService();
const windowWidth = ref(0);
const langArea = ref<HTMLElement | null>(null);
const isWideScreen = computed(() => windowWidth.value > 767);

const keysOfLanguages = computed((): Array<string> => {
  return Object.keys(LocalLanguagesType);
});

const copyright = computed(() =>
    t('corporation', {year: new Date().getUTCFullYear()})
);

onMounted(() => {
  windowWidth.value = window.innerWidth;
  window.addEventListener("resize", () => {
    windowWidth.value = window.innerWidth;
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", () => {
    windowWidth.value = window.innerWidth;
  });
});

const methods = {
  menuLabel(item: { title: string; navKey?: string }) {
    return item.navKey ? navText(item.navKey) : t(`menu.${item.title}`);
  },
  setActiveClass(lang) {
    return (LocalLanguagesType[lang] === LocalLanguagesType[$i18n.locale.value]) ? "active" : "";
  },
  changeLocale(locale: "es" | "en" | "vi" | "ko" | "ja") {
    if(userStore.isLogin()) {
      userService.setUserLocale(locale).then(() => methods.setBrowserLocale(locale));
    } else {
      methods.setBrowserLocale(locale);
    }
  },
  setBrowserLocale(locale: string) {
    window.scrollTo(0, 0);
    $i18n.setLocale(locale);
  },
  onClickMenu(menuTitle: string) {
    emit("onClickMenu", menuTitle, "footer");
  }
};

onClickOutside(() => langArea.value, () => {
    isActive.value = false;
})

useHead({
  htmlAttrs: {
    lang: $i18n.locale.value
  }
})
</script>

<i18n>
{
  "en": {
    "menu": {
      "get_started": "Get Started",
      "request_demo": "Request a Demo",
      "contact": "Contact Us"
    },
    "terms-of-use": "Terms of Use",
    "legal-notice": "Legal Notice",
    "privacy-policy": "Privacy Policy",
    "cookie-policy": "Cookie Policy",
    "contact-mail": "{account}{'@'}{domain}",
    "email": "E-mail : ",
    "corporation": "ⓒ {year} Codepresso, Inc."
  },
  "ko": {
    "menu": {
      "get_started": "시작하기",
      "request_demo": "무료 체험하기",
      "contact": "문의하기"
    },
    "terms-of-use": "이용약관",
    "legal-notice": "법적 고지",
    "privacy-policy": "개인정보처리방침",
    "cookie-policy": "쿠키 정책",
    "contact-mail": "{account}{'@'}{domain}",
    "email": "E-mail : ",
    "corporation": "ⓒ {year} Codepresso, Inc."
  },
  "ja": {
    "menu": {
      "get_started": "始める",
      "request_demo": "デモをリクエスト",
      "contact": "お問い合わせ"
    },
    "terms-of-use": "利用規約",
    "legal-notice": "法的通知",
    "privacy-policy": "プライバシーポリシー",
    "cookie-policy": "クッキーポリシー",
    "contact-mail": "{account}{'@'}{domain}",
    "email": "E-mail : ",
    "corporation": "ⓒ {year} Codepresso, Inc."
  }
}
</i18n>
<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/footer.scss";
</style>
